import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Tag } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { api } from "@/lib/api";

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    api
      .get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        setStatus("ready");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <p data-testid="article-loading" className="eyebrow text-ink-faint">
          Loading…
        </p>
      </div>
    );
  }
  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center gap-6 p-8">
        <p className="eyebrow text-gold">404</p>
        <h1 className="font-serif text-4xl text-navy">Article not found.</h1>
        <button
          onClick={() => navigate("/insights")}
          data-testid="btn-back-insights"
          className="eyebrow text-navy border-b border-navy hover:text-gold hover:border-gold transition-colors pb-1"
        >
          Back to all insights →
        </button>
      </div>
    );
  }

  return (
    <div data-testid="page-article" className="bg-paper min-h-screen">
      <SEO
        title={article.title}
        description={article.excerpt || article.subtitle || ""}
        image={article.hero_image}
        type="article"
      />
      <Navbar />

      {/* HERO */}
      <section className="relative bg-navy text-paper pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {article.hero_image && (
          <>
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage: `url('${article.hero_image}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(5,28,44,0.7) 0%, rgba(5,28,44,0.95) 100%)",
              }}
              aria-hidden="true"
            />
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-9">
            <Link
              to="/insights"
              data-testid="link-back-insights"
              className="inline-flex items-center gap-2 eyebrow text-gold hover:text-paper transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              All insights
            </Link>

            <p className="eyebrow text-gold mb-6">{article.category}</p>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-paper mb-6"
              data-testid="article-title"
            >
              {article.title}
            </motion.h1>

            {article.subtitle && (
              <p className="text-lg md:text-xl text-paper/70 leading-relaxed max-w-3xl mb-8">
                {article.subtitle}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-6 text-sm text-paper/60">
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gold" strokeWidth={1.5} />
                {article.read_time_minutes} min read
              </span>
              <span className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-gold" strokeWidth={1.5} />
                {(article.tags || []).join(" · ")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* BODY — two-zone McKinsey layout */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
          <aside className="md:col-span-3">
            <div className="sticky top-32 space-y-6">
              <div>
                <p className="eyebrow text-ink-faint mb-2">Author</p>
                <p className="font-serif text-lg text-navy">
                  Nimisha Mohan Srivastava
                </p>
              </div>
              <div>
                <p className="eyebrow text-ink-faint mb-2">Category</p>
                <p className="text-sm text-navy">{article.category}</p>
              </div>
              <div>
                <p className="eyebrow text-ink-faint mb-2">Read time</p>
                <p className="text-sm text-navy">
                  {article.read_time_minutes} minutes
                </p>
              </div>
              <div className="pt-4 border-t border-rule">
                <p className="eyebrow text-ink-faint mb-3">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {(article.tags || []).map((t) => (
                    <span
                      key={t}
                      className="eyebrow text-navy border border-rule px-2.5 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <article
            data-testid="article-body"
            className="md:col-span-9 prose-editorial max-w-none"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {article.content_markdown}
            </ReactMarkdown>

            <hr />

            <div className="not-prose mt-12 border border-navy p-8 bg-cream">
              <p className="eyebrow text-gold mb-3">Discuss</p>
              <h3 className="font-serif text-2xl text-navy mb-4">
                Want to take this further?
              </h3>
              <p className="text-ink-muted mb-6">
                I advise boards, founders and executive teams on capital-efficient
                scale, revenue architecture and AI-native operating models.
              </p>
              <div className="flex flex-wrap gap-6">
                <a
                  href="mailto:nimisham@umich.edu"
                  data-testid="article-cta-email"
                  className="eyebrow text-navy border-b border-navy hover:text-gold hover:border-gold pb-1 transition-colors"
                >
                  nimisham@umich.edu →
                </a>
                <a
                  href="https://www.linkedin.com/in/nimimohan"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="article-cta-linkedin"
                  className="eyebrow text-navy border-b border-navy hover:text-gold hover:border-gold pb-1 transition-colors"
                >
                  Connect on LinkedIn →
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
